import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  useAssignEveryoneToManager,
  useAssignManagerToResource,
  useRemoveSubordinates,
  useUnAssignManagerFromResource,
} from "@planzone/view-models";
import { ResourceAvatar } from "@common/resourceAvatar";
import { SelectList } from "@common/selectList";
import { ResourceAllocationMenu } from "@common/resourceAllocationMenu";
import { AddResourceIcon } from "@scenes/project/resources/menu/addResourceIcon";
import { Props } from "./types";
import styles from "./styles.scss";

function Subordinates({ resource, isCompact = false }: Props) {
  const { t } = useTranslation();
  const subordinates = resource.subordinates ?? [];
  const { assignManagerToResource } = useAssignManagerToResource(resource);
  const { unAssignManagerFromResource } =
    useUnAssignManagerFromResource(resource);
  const { assignEveryoneToManager, isRequestPending: isEveryonePending } =
    useAssignEveryoneToManager(resource);
  const { removeSubordinates, isRequestPending: isRemovalPending } =
    useRemoveSubordinates(resource);

  return (
    <div
      className={classNames(styles.subordinates, {
        [styles.compact]: isCompact,
      })}
      onClick={(e) => e.stopPropagation()}
    >
      <SelectList
        rootClassName={styles.addAllocation}
        listClassName={styles.selectList}
      >
        {() => ({
          input:
            subordinates.length > 0 ? (
              <ul className={styles.list}>
                {subordinates.slice(0, 2).map((subordinate) => (
                  <li key={subordinate.uuid} className={styles.item}>
                    <div className={styles.avatar}>
                      <ResourceAvatar resource={subordinate} />
                    </div>
                    <div className={styles.infoBubble}>
                      <p>{subordinate.name}</p>
                    </div>
                  </li>
                ))}
                {subordinates.length > 2 && (
                  <li className={`${styles.item} ${styles.summary}`}>
                    <p>+{subordinates.length - 2}</p>
                    <div className={styles.infoBubble}>
                      {subordinates.slice(2).map((subordinate) => (
                        <p key={subordinate.uuid}>{subordinate.name}</p>
                      ))}
                    </div>
                  </li>
                )}
              </ul>
            ) : (
              <div className={styles.iconBox}>
                <AddResourceIcon className={styles.icon} />
              </div>
            ),
          list: (
            <div>
              <ResourceAllocationMenu
                allocatedResources={subordinates}
                openedViews={["resources"]}
                select={(r) =>
                  r.map((value) =>
                    assignManagerToResource({
                      resource: value,
                      manager: resource,
                    }),
                  )
                }
                unSelect={(r) =>
                  r.map((value) =>
                    unAssignManagerFromResource({
                      resource: value,
                      manager: resource,
                    }),
                  )
                }
                everyone={{
                  select: () => assignEveryoneToManager({ manager: resource }),
                  isPending: isEveryonePending,
                  excludedResource: resource,
                }}
              />
              {subordinates.length > 0 && (
                <button
                  type="button"
                  className={styles.clearButton}
                  onClick={() =>
                    !isRemovalPending &&
                    removeSubordinates({ manager: resource })
                  }
                >
                  {t("planzoneMembersPage.resources.clearSubordinates")}
                </button>
              )}
            </div>
          ),
        })}
      </SelectList>
    </div>
  );
}

export { Subordinates };
